import React from 'react';
import { UserInfo } from '../types';
import { loadScript } from '../utils/helpers';

export interface BaseMFEProps extends CommonAttrs {
  exposedPath: string;
  name: string;
  entryPointUrl: string;
}

export interface BaseMFEMountParams extends CommonAttrs {}

export interface BaseMFEMountResponse {}

interface CommonAttrs {
  userInfo: UserInfo;
}

type MountType<T, R> = (el: Element, params: T) => R;
type UnmountType = (ref: unknown) => void;

interface BaseMFEApp<T, R> {
  mount: MountType<T, R>;
  unmount?: UnmountType;
}

type MyState = {
  isLoading: boolean;
  isError: boolean;
  unmount?: UnmountType;
};

export abstract class BaseMicroFrontend<
  T extends BaseMFEProps = any,
> extends React.Component {
  declare props: T;

  componentRef: React.RefObject<unknown>;
  state: MyState = {
    isLoading: true,
    isError: false,
  };
  constructor(props: T) {
    super(props);
    this.props = props;
    this.componentRef = React.createRef();
  }

  abstract buildMountParams(): BaseMFEMountParams;
  abstract afterMount(response: BaseMFEMountResponse): void;

  afterLoad(app: () => BaseMFEApp<BaseMFEMountParams, BaseMFEMountResponse>) {
    const params = this.buildMountParams();

    const { mount, unmount } = app();
    this.setState({ unmount, isLoading: false });

    const response = mount(this.componentRef.current as Element, params);
    this.afterMount(response);
  }

  async componentDidMount() {
    const { exposedPath, name, entryPointUrl } = this.props;

    try {
      await loadScript(name, entryPointUrl);
      const app = await window[name].get(exposedPath);

      this.afterLoad(app);

      console.log(`${name}-${exposedPath}: componentDidMount`);
    } catch (error) {
      console.log(`${name}-${exposedPath}: Error ${error}`);
      this.setState({ isLoading: false, isError: true });
    }
  }

  componentWillUnmount() {
    const { unmount } = this.state;
    const el = this.componentRef.current;
    if (unmount && el) unmount(el);
  }

  render() {
    const { isLoading, isError } = this.state;
    return (
      <div>
        {isLoading && '...'}
        {isError && 'There was a problem loading this section'}
        <div ref={node => ((this.componentRef as any).current = node)} />
      </div>
    );
  }
}
